// pages/FAQPage/FAQPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaMinus, FaQuestionCircle } from 'react-icons/fa';
import './css/FAQPage.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../../config/api';

// Helper Component for Accordion Item
const AccordionItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`accordion-item ${isOpen ? 'open' : ''}`}>
      <button className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <span className="question-text">
          <FaQuestionCircle className="icon" /> {question}
        </span>
        <span className="toggle-icon">{isOpen ? <FaMinus /> : <FaPlus />}</span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="accordion-body"
          >
            <div className="answer-content" style={{ whiteSpace: 'pre-line' }}>
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  const defaultSection = useMemo(
    () => ({
      header_title: 'الأسئلة الشائعة',
      header_subtitle: 'كل ما تحتاجين معرفته عن خدماتنا وسياسات العمل.',
      footer_title: 'لم تجدي إجابة لسؤالك؟',
      footer_subtitle: 'لا تترددي في التواصل معي مباشرة عبر الواتساب.',
      footer_button_text: 'تواصلي معنا',
      footer_button_link: '/contact',
    }),
    []
  );

  const [section, setSection] = useState(defaultSection);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [sectionRes, faqsRes] = await Promise.all([
          axios.get(`${API_BASE}/api/faq/section`),
          axios.get(`${API_BASE}/api/faq`),
        ]);

        if (cancelled) return;

        const nextSection = sectionRes?.data?.section;
        if (nextSection) setSection({ ...defaultSection, ...nextSection });

        const nextFaqs = Array.isArray(faqsRes?.data?.faqs) ? faqsRes.data.faqs : [];
        setFaqs(nextFaqs);
      } catch (err) {
        // keep fallbacks on any error
        console.error('FAQ load error:', err);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [defaultSection]);

  return (
    <div className="faq-page" dir="rtl">
      
      {/* Header */}
      <header className="faq-header">
        <div className="container text-center relative-z">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {section.header_title}
          </motion.h1>
          <p>{section.header_subtitle}</p>
        </div>
      </header>

      {/* Accordion Section */}
      <section className="faq-content">
        <div className="container">
          <div className="faq-wrapper">
            {faqs.map((item, index) => (
              <AccordionItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
          
          {/* Still have questions? */}
          <div className="faq-footer">
            <h3>{section.footer_title}</h3>
            <p>{section.footer_subtitle}</p>
            <Link to={section.footer_button_link || '/contact'} className="btn-contact-link">
              {section.footer_button_text}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FAQPage;